// Persisted cosmetic IDs. Mirrored and validated by agentAppearance.js on the API.
// `group` sorts the chair's picker. `first` is the nearest cut of the first
// version: the API only knows those four until it takes the rest
// (docs/BAILEY_STYLE_CHAIR_BACKEND.md), so a save sends it meanwhile and
// the real cut is kept with the chair's other newer fields (lookStore.js).
export const HAIRSTYLES = [
    { id: 'none', name: 'The original', detail: 'A clean canvas.', group: null },
    { id: 'fade', name: 'Fresh fade', detail: 'Clean sides. Texture up top.', group: 'Short' },
    { id: 'buzz', name: 'The buzz', detail: 'Short, even and sharp.', group: 'Short', first: 'fade' },
    { id: 'quiff', name: 'The quiff', detail: 'Height at the front, clean sides.', group: 'Short', first: 'fade' },
    { id: 'pixie', name: 'The pixie', detail: 'Cropped close, a sweep of fringe.', group: 'Short', first: 'fade' },
    { id: 'curls', name: 'Full curls', detail: 'Volume, shape and a little colour.', group: 'Short' },
    { id: 'afro', name: 'The afro', detail: 'Big, round and full of coils.', group: 'Short', first: 'curls' },
    { id: 'bob', name: 'The bob', detail: 'Soft movement. A sharp finish.', group: 'Long' },
    { id: 'sleek', name: 'Long and sleek', detail: 'Glossy lengths, a clean middle part.', group: 'Long', first: 'bob' },
    { id: 'waves', name: 'Beach waves', detail: 'Loose waves with a soft side part.', group: 'Long', first: 'bob' },
    { id: 'pony', name: 'High pony', detail: 'Pulled up high, with a swing.', group: 'Long', first: 'bob' },
    { id: 'braids', name: 'Box braids', detail: 'Long braids with a few gold cuffs.', group: 'Long', first: 'bob' }
];
export const HAIR_GROUPS = ['Short', 'Long'];
/** The first version's cut nearest to this one (the API's four). */
export const firstCut = id => { const s = HAIRSTYLES.find(h => h.id === id); return s ? s.first || s.id : 'none'; };
export const isNewCut = id => HAIRSTYLES.some(h => h.id === id && !!h.first);
// A cut listed here isn't offered in the style chair, and a look wearing it
// shows its nearest first-version cut. All twelve are offered since the
// rebuild (30 Sept 2026); put an id back here to hide one again.
export const PARKED_CUTS = [];
export const OFFERED_HAIRSTYLES = HAIRSTYLES.filter(h => !PARKED_CUTS.includes(h.id));
export const wearable = look => (look && PARKED_CUTS.includes(look.hairStyle) ? { ...look, hairStyle: firstCut(look.hairStyle) } : look);
// How the second hair colour is placed. `second` names that colour in the UI.
export const HAIR_TECHNIQUES = [
    { id: 'solid', name: 'Solid', second: null },
    { id: 'highlights', name: 'Highlights', second: 'Highlights' },
    { id: 'balayage', name: 'Balayage', second: 'Ends' },
    { id: 'tips', name: 'Dipped tips', second: 'Tips' }
];
export const TATTOO_IDS = ['none', 'scissors', 'comb', 'rose', 'mum', 'swallow', 'star', 'moon', 'lightning', 'butterfly', 'anchor', 'vine', 'dots'];
export const TATTOO_INKS = [
    { id: 'black', name: 'Black' },
    { id: 'fineline', name: 'Fine line' },
    { id: 'colour', name: 'Colour' }
];
export const COLOUR_FIELDS = ['hairColour', 'hairAccent', 'shirtColour', 'nailColour', 'linerColour', 'lipColour', 'blushColour'];
// Added in the style chair's second version. The API does not store these yet
// (docs/BAILEY_STYLE_CHAIR_BACKEND.md); lookStore.js keeps them meanwhile.
export const EXTRA_FIELDS = ['hairTechnique', 'tattooLeft', 'tattooRight', 'tattooInk', 'linerOn', 'lipsOn', 'blushOn'];
export const DEFAULT_APPEARANCE = {
    look: 'original', hairStyle: 'none', hairColour: '#302820', hairAccent: '#302820',
    shirtColour: '#29282d', nailsEnabled: false, nailColour: '#9d435c',
    makeupEnabled: false, linerColour: '#425ed1', lipColour: '#9d435c', blushColour: '#d69998',
    // Defaults reproduce the first version exactly: every makeup part on,
    // accent strands as highlights and no ink.
    hairTechnique: 'highlights', tattooLeft: 'none', tattooRight: 'none', tattooInk: 'black',
    linerOn: true, lipsOn: true, blushOn: true
};
const oneOf = (list, value, fallback) => (list.indexOf(value) !== -1 ? value : fallback);
export function normaliseAppearance(input) {
    const raw = typeof input === 'string' ? { look: input } : input || {};
    const look = ['original', 'fade', 'curls', 'bob'].includes(raw.look) ? raw.look : 'original';
    const defaults = { ...DEFAULT_APPEARANCE, look,
        hairStyle: look === 'original' ? 'none' : look,
        nailsEnabled: look === 'bob', makeupEnabled: look === 'bob',
        hairColour: look === 'curls' ? '#df9baf' : '#302820',
        hairAccent: look === 'curls' ? '#a899c6' : '#302820'
    };
    const appearance = { ...defaults };
    if (HAIRSTYLES.some(style => style.id === raw.hairStyle)) appearance.hairStyle = raw.hairStyle;
    COLOUR_FIELDS.forEach(key => {
        if (typeof raw[key] === 'string' && /^#[0-9a-f]{6}$/i.test(raw[key])) appearance[key] = raw[key].toLowerCase();
    });
    ['nailsEnabled', 'makeupEnabled', 'linerOn', 'lipsOn', 'blushOn'].forEach(key => { if (typeof raw[key] === 'boolean') appearance[key] = raw[key]; });
    appearance.hairTechnique = oneOf(HAIR_TECHNIQUES.map(t => t.id), raw.hairTechnique, appearance.hairTechnique);
    appearance.tattooLeft = oneOf(TATTOO_IDS, raw.tattooLeft, 'none');
    appearance.tattooRight = oneOf(TATTOO_IDS, raw.tattooRight, 'none');
    appearance.tattooInk = oneOf(TATTOO_INKS.map(i => i.id), raw.tattooInk, 'black');
    // Makeup with every part off is simply no makeup.
    if (!appearance.linerOn && !appearance.lipsOn && !appearance.blushOn) {
        appearance.makeupEnabled = false;
        appearance.linerOn = appearance.lipsOn = appearance.blushOn = true;
    }
    return appearance;
}
export function appearanceKey(value) { return JSON.stringify(normaliseAppearance(value)); }

// Relative luminance (WCAG), from a #rrggbb string in sRGB.
export function luminance(hex) {
    const n = parseInt(String(hex).slice(1), 16);
    const lin = c => { const s = c / 255; return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}
export function contrast(a, b) {
    const la = luminance(a), lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
export const LOGO_LIGHT = '#f7f3ec';
export const LOGO_DARK = '#242226';
// The tee's logo print: whichever of cream and ink stands out more.
export function logoInk(shirtColour) {
    return contrast(shirtColour, LOGO_DARK) >= contrast(shirtColour, LOGO_LIGHT) ? LOGO_DARK : LOGO_LIGHT;
}
